import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TFunction } from 'i18next';
import { message, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import EditSubscriptionPlanHeader from './EditSubscriptionPlanHeader';
import PlanInfoSection from './PlanInfoSection';
import PlanBenefitsSection from './PlanBenefitsSection';
import PlanPricingSection from './PlanPricingSection';
import BenefitModal, { BenefitFormValues } from './BenefitModal';
import {
  useGetMarketplacePlanByIdQuery,
  useUpdateMarketplacePlanMutation,
} from '../../../store/services/marketplacePlansService';

export const editPlanSchema = (t: TFunction) =>
  z
    .object({
      nameEn: z.string().optional(),
      nameAr: z.string().optional(),
      productLimit: z.coerce
        .number({ invalid_type_error: t('acceptNumbersOnly') })
        .int(t('acceptNumbersOnly'))
        .min(1, t('acceptNumbersOnly')),
      monthlySar: z.coerce
        .number({ invalid_type_error: t('acceptNumbersOnly') })
        .min(0, t('acceptNumbersOnly')),
      monthlyCoins: z.coerce
        .number({ invalid_type_error: t('acceptNumbersOnly') })
        .int(t('acceptNumbersOnly'))
        .min(0, t('acceptNumbersOnly')),
      yearlySar: z.coerce
        .number({ invalid_type_error: t('acceptNumbersOnly') })
        .min(0, t('acceptNumbersOnly')),
      yearlyCoins: z.coerce
        .number({ invalid_type_error: t('acceptNumbersOnly') })
        .int(t('acceptNumbersOnly'))
        .min(0, t('acceptNumbersOnly')),
      extraFeeMonthlyCoins: z.union([z.string(), z.number()]).nullish(),
      extraFeeMonthlySar: z.union([z.string(), z.number()]).nullish(),
      extraFeeYearlyCoins: z.union([z.string(), z.number()]).nullish(),
      extraFeeYearlySar: z.union([z.string(), z.number()]).nullish(),
      benefits: z
        .array(z.object({ textEn: z.string(), textAr: z.string() }))
        .max(5),
      recommended: z.boolean(),
      allowExtraProducts: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (
        data.monthlySar !== undefined &&
        data.yearlySar !== undefined &&
        Number(data.monthlySar) >= Number(data.yearlySar)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('home.yearly-price-must-be-greater-than-monthly'),
          path: ['yearlySar'],
        });
      }

      if (
        data.monthlyCoins !== undefined &&
        data.yearlyCoins !== undefined &&
        Number(data.monthlyCoins) >= Number(data.yearlyCoins)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('home.yearly-coins-must-be-greater-than-monthly'),
          path: ['yearlyCoins'],
        });
      }

      if (data.allowExtraProducts) {
        const checkFee = (
          val: string | number | null | undefined,
          fieldName: 'extraFeeMonthlyCoins' | 'extraFeeMonthlySar' | 'extraFeeYearlyCoins' | 'extraFeeYearlySar',
          isInt = false
        ) => {
          if (val === undefined || val === null || String(val).trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('requiredField'),
              path: [fieldName],
            });
            return false;
          }
          const num = Number(val);
          if (isNaN(num) || num < 0 || (isInt && !Number.isInteger(num))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('acceptNumbersOnly'),
              path: [fieldName],
            });
            return false;
          }
          return true;
        };

        const validMonthlyCoins = checkFee(data.extraFeeMonthlyCoins, 'extraFeeMonthlyCoins', true);
        const validMonthlySar = checkFee(data.extraFeeMonthlySar, 'extraFeeMonthlySar');
        const validYearlyCoins = checkFee(data.extraFeeYearlyCoins, 'extraFeeYearlyCoins', true);
        const validYearlySar = checkFee(data.extraFeeYearlySar, 'extraFeeYearlySar');

        if (
          validMonthlySar &&
          validYearlySar &&
          Number(data.extraFeeMonthlySar) >= Number(data.extraFeeYearlySar)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('home.extra-fee-yearly-must-be-greater-than-monthly'),
            path: ['extraFeeYearlySar'],
          });
        }

        if (
          validMonthlyCoins &&
          validYearlyCoins &&
          Number(data.extraFeeMonthlyCoins) >= Number(data.extraFeeYearlyCoins)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('home.extra-fee-yearly-coins-must-be-greater-than-monthly'),
            path: ['extraFeeYearlyCoins'],
          });
        }
      }
    });

export type FormValues = z.infer<ReturnType<typeof editPlanSchema>>;

export default function EditSubscriptionPlanPage() {
  const { t } = useTranslation();
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();

  const { data: planResponse, isLoading: isFetching } = useGetMarketplacePlanByIdQuery(
    planId || '',
    { skip: !planId }
  );
  const [updateMarketplacePlan, { isLoading: isSaving }] =
    useUpdateMarketplacePlanMutation();

  const currentPlan = planResponse?.payload;

  // Modal for adding/editing a benefit
  const [benefitModalOpen, setBenefitModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const validationSchema = useMemo(
    () => editPlanSchema(t),
    [t]
  );

  const methods = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      nameEn: '',
      nameAr: '',
      productLimit: 1,
      monthlySar: 0,
      monthlyCoins: 0,
      yearlySar: 0,
      yearlyCoins: 0,
      extraFeeMonthlyCoins: '',
      extraFeeMonthlySar: '',
      extraFeeYearlyCoins: '',
      extraFeeYearlySar: '',
      benefits: [],
      recommended: false,
      allowExtraProducts: false,
    },
  });

  const { isDirty } = methods.formState;
  const { fields, append, remove, update } = useFieldArray({
    control: methods.control,
    name: 'benefits',
  });

  // Watch allowExtraProducts to pass to PlanPricingSection
  const allowExtraProducts = methods.watch('allowExtraProducts');
  const recommended = methods.watch('recommended');

  useEffect(() => {
    if (currentPlan) {
      methods.reset({
        nameEn: currentPlan.planNameEn || '',
        nameAr: currentPlan.planNameAr || '',
        productLimit: currentPlan.productLimit ?? 1,
        monthlySar: currentPlan.priceMonthly ?? 0,
        monthlyCoins: currentPlan.coinsMonthly ?? 0,
        yearlySar: currentPlan.priceYearly ?? 0,
        yearlyCoins: currentPlan.coinsYearly ?? 0,
        extraFeeMonthlyCoins: currentPlan.extraFeeMonthlyCoins ?? '',
        extraFeeMonthlySar: currentPlan.extraFeeMonthlyPrice ?? '',
        extraFeeYearlyCoins: currentPlan.extraFeeYearlyCoins ?? '',
        extraFeeYearlySar: currentPlan.extraFeeYearlyPrice ?? '',
        benefits: (currentPlan.informationsEn || []).map((textEn, idx) => ({
          textEn,
          textAr: currentPlan.informationsAr?.[idx] || '',
        })),
        recommended: currentPlan.recommended || false,
        allowExtraProducts: currentPlan.allowExtraProducts ?? false,
      });
    }
  }, [currentPlan, methods]);

  const handleToggleAllowExtraProducts = (checked: boolean) => {
    methods.setValue('allowExtraProducts', checked, { shouldDirty: true });
    if (!checked) {
      methods.clearErrors([
        'extraFeeMonthlyCoins',
        'extraFeeMonthlySar',
        'extraFeeYearlyCoins',
        'extraFeeYearlySar',
      ]);
    }
  };

  const handleOpenAddBenefit = () => {
    if (fields.length >= 5) return;
    setEditingIndex(null);
    setBenefitModalOpen(true);
  };

  const handleOpenEditBenefit = (_benefit: { textEn: string; textAr: string }, index: number) => {
    setEditingIndex(index);
    setBenefitModalOpen(true);
  };

  const handleDeleteBenefit = (index: number) => {
    remove(index);
  };

  const handleSaveBenefit = (values: BenefitFormValues) => {
    if (editingIndex !== null) {
      update(editingIndex, { textEn: values.benefitTextEn, textAr: values.benefitTextAr });
    } else {
      append({ textEn: values.benefitTextEn, textAr: values.benefitTextAr });
    }
    setBenefitModalOpen(false);
  };

  const onSubmit = async (data: FormValues) => {
    if (!currentPlan) return;

    try {
      await updateMarketplacePlan({
        id: currentPlan.id,
        body: {
          productLimit: Number(data.productLimit),
          recommended: data.recommended,
          informationsEn: data.benefits.map((b) => b.textEn),
          informationsAr: data.benefits.map((b) => b.textAr),
          priceMonthly: Number(data.monthlySar) || 0,
          priceYearly: Number(data.yearlySar) || 0,
          coinsMonthly: Number(data.monthlyCoins) || 0,
          coinsYearly: Number(data.yearlyCoins) || 0,
          allowExtraProducts: data.allowExtraProducts,
          extraFeeMonthlyPrice: data.allowExtraProducts ? Number(data.extraFeeMonthlySar) || 0 : null,
          extraFeeMonthlyCoins: data.allowExtraProducts ? Number(data.extraFeeMonthlyCoins) || 0 : null,
          extraFeeYearlyPrice: data.allowExtraProducts ? Number(data.extraFeeYearlySar) || 0 : null,
          extraFeeYearlyCoins: data.allowExtraProducts ? Number(data.extraFeeYearlyCoins) || 0 : null,
        },
      }).unwrap();

      message.success(t('home.plan-updated-success'));
      navigate('/subscriptions-plans');
    } catch {
      message.error(t('home.plan-update-failed'));
    }
  };

  // Map useFieldArray fields to the shape PlanBenefitsSection expects
  const benefitItems = fields.map((f, idx) => ({
    id: f.id,
    textEn: f.textEn,
    textAr: f.textAr,
    _idx: idx,
  }));

  // Derive editingBenefit for BenefitModal from fields + editingIndex
  const editingBenefitForModal =
    editingIndex !== null && fields[editingIndex]
      ? { id: fields[editingIndex].id, textEn: fields[editingIndex].textEn, textAr: fields[editingIndex].textAr }
      : null;

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="edit-subscription-plan-page-container"
    >
      {/* Header with Title, Breadcrumbs & Save Button */}
      <EditSubscriptionPlanHeader
        formId="edit-subscription-plan-form"
        isLoading={isSaving}
        disabled={!isDirty || isSaving}
      />

      {/* Main Form Card */}
      <div
        className="w-full max-w-[1000px] mx-auto bg-light-surface-primary dark:bg-dark-surface-primary rounded-[12px] p-6 flex flex-col gap-6 shadow-sm border border-light-input-border dark:border-dark-input-border"
        data-testid="edit-plan-card"
      >
        {isFetching ? (
          <div data-testid="edit-plan-skeleton-loading">
            <Skeleton active paragraph={{ rows: 12 }} />
          </div>
        ) : (
          <FormProvider {...methods}>
            <form
              id="edit-subscription-plan-form"
              onSubmit={methods.handleSubmit(onSubmit)}
              className="flex flex-col gap-6"
              data-testid="edit-plan-form"
            >
              {/* Section 1: Plan Information */}
              <PlanInfoSection />

              {/* Sub-section: Benefits List & Mark as Recommended */}
              <PlanBenefitsSection
                benefits={benefitItems}
                isRecommended={recommended}
                onOpenAddBenefit={handleOpenAddBenefit}
                onOpenEditBenefit={handleOpenEditBenefit}
                onDeleteBenefit={handleDeleteBenefit}
                onToggleRecommended={(checked) => methods.setValue('recommended', checked, { shouldDirty: true })}
              />

              {/* Divider */}
              <div
                className="w-full border-t border-light-input-border dark:border-dark-input-border my-1"
                data-testid="plan-sections-divider"
              />

              {/* Section 2: Plan Pricing & Extra Fees */}
              <PlanPricingSection
                allowExtraProducts={allowExtraProducts}
                onToggleAllowExtraProducts={handleToggleAllowExtraProducts}
              />
            </form>
          </FormProvider>
        )}
      </div>

      {/* Benefit Add/Edit Modal */}
      <BenefitModal
        isOpen={benefitModalOpen}
        editingBenefit={editingBenefitForModal}
        onClose={() => setBenefitModalOpen(false)}
        onSave={handleSaveBenefit}
      />
    </div>
  );
}
